import React from "react";
import { Badge, Box, HStack, Stack, Text } from "@chakra-ui/react";
import { ChartCard, DataTable, StatusIcon } from "./parts";
import { useVizTokens } from "./tokens";
import { count, dateTime, money } from "./format";

// The patients texting costs the most, with each one's number status: an opted-out or bad number
// that is still texted is money spent on nothing.

// The table's words, by the direction shown.
const WORDS = {
  outbound: { title: "Patients texted the most", who: "patients texted", last: "Last texted" },
  inbound: { title: "Patients who texted in the most", who: "patients who texted in", last: "Last texted in" },
  all: { title: "Patients texting the most", who: "patients texting or texted", last: "Last text" },
};

const VERDICTS = {
  opt_out: { status: "serious", words: "Opted out" },
  bad: { status: "critical", words: "Bad number" },
  good: { status: "good", words: "Delivers" },
};

// Why some rows have no name: their texts are ones this database has no record of (the server's
// "_outside" clinic), to a number no patient here has. Empty when none is listed.
function outsideWords(report) {
  if (!(report.patients || []).some((p) => p.clinic === "_outside" && !p.patient_id)) return "";
  return ' "Not in this database": no SMS record or send event here names the text, and no patient here has the number.';
}

function CostBar({ value, max, tokens }) {
  return (
    <HStack spacing={2} justify="flex-end">
      <Box w="90px" display="flex" justifyContent="flex-start">
        <Box h="8px" w={`${max > 0 ? Math.max(2, (value / max) * 100) : 0}%`} bg={tokens.series[0]} borderRightRadius="4px" />
      </Box>
      <Text as="span" minW="56px" textAlign="right">{money(value)}</Text>
    </HStack>
  );
}

export default function PatientsPanel({ report }) {
  const t = useVizTokens();
  const patients = report.patients || [];
  const max = Math.max(0, ...patients.map((p) => p.cost));
  const words = WORDS[report.direction] || WORDS.outbound;
  return (
    <Stack spacing={4}>
      <ChartCard title={words.title}
                 subtitle={`The ${patients.length} costliest of ${count(report.totals.patients)} ${words.who} in this range`}>
        <DataTable
          columns={[
            { key: "name", label: "Patient" },
            { key: "phone", label: "Phone" },
            { key: "clinic_label", label: "Clinic", wrap: true },
            { key: "texts", label: "Texts", numeric: true, render: (p) => count(p.texts) },
            { key: "avg_segments", label: "Segments each", numeric: true },
            { key: "cost", label: "Cost", numeric: true, render: (p) => <CostBar value={p.cost} max={max} tokens={t} /> },
            { key: "last_sent", label: words.last, render: (p) => dateTime(p.last_sent) },
            { key: "verdict", label: "Number", render: (p) => {
              const v = VERDICTS[p.verdict];
              if (!v) return <Text as="span" color={t.muted}>-</Text>;
              return (
                <HStack spacing={1.5}>
                  <StatusIcon status={v.status} size={14} />
                  <Badge variant="subtle" colorScheme={v.status === "good" ? "green" : v.status === "critical" ? "red" : "orange"}>
                    {v.words}
                  </Badge>
                </HStack>
              );
            } },
          ]}
          rows={patients}
        />
        <Text fontSize="xs" color={t.muted} mt={2}>
          Names come from every clinic's appointments here, matched on the cell or home phone however it was typed.
          {outsideWords(report)} Phone numbers are shortened to their last four digits.
        </Text>
      </ChartCard>
    </Stack>
  );
}
